import React from 'react';
import { useParams, Link, Navigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Shield, Phone, Printer, Server, Brain, GraduationCap, MapPin, Clock, ArrowRight, CheckCircle } from 'lucide-react';
import locations from '../data/locations';
import SEO from '../components/SEO';
import { SEO_TEXTES } from '../data/seoTextes';

const fadeUp = {
  initial: { opacity: 0, y: 24 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: '-50px' },
  transition: { duration: 0.5, ease: 'easeOut' },
};

const SERVICES = [
  {
    slug: 'infogerance-cyber',
    icon: Shield,
    title: 'Infogérance & cybersécurité',
    description: "Supervision 24/7, EDR, sauvegarde 3-2-1, plan de reprise testé.",
  },
  {
    slug: 'telephonie',
    icon: Phone,
    title: "Téléphonie d'entreprise",
    description: 'Standard Yeastar, softphone Linkus, économies 30 à 50 % sur la facture télécom.',
  },
  {
    slug: 'copieurs',
    icon: Printer,
    title: 'Copieurs & impression managée',
    description: 'Multi-marques, maintenance et consommables inclus, audit gratuit.',
  },
  {
    slug: 'serveurs-infrastructure',
    icon: Server,
    title: 'Serveurs & infrastructure',
    description: 'Audit, virtualisation, cloud hybride, sauvegarde Veeam, supervision.',
  },
  {
    slug: 'ia-automatisation',
    icon: Brain,
    title: 'IA & automatisation',
    description: 'IA souveraine, hébergement européen, ROI mesurable dès les premiers mois.',
  },
  {
    slug: 'formation',
    icon: GraduationCap,
    title: 'Formations IT et IA',
    description: 'Certifiées Qualiopi, finançables OPCO, dispensées par MCF Formation.',
  },
];

export default function ExpertiseLocale() {
  const { ville } = useParams();
  const loc = locations[ville];

  if (!loc) {
    return <Navigate to="/" replace />;
  }

  // Titre et description propres à chaque ville : src/data/seoTextes.js (une entrée par ville de locations.js, test
  // dans seoTextes.test.js), les mêmes que le repli du pré-rendu et llms.txt. Le reste de la page vient de locations.js.
  const seo = SEO_TEXTES[`/expertise-locale/${ville}`] || {};

  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Service',
    serviceType: 'Services informatiques pour entreprises',
    provider: {
      '@type': 'Organization',
      name: 'METERIS',
      url: 'https://meteris.fr',
      address: {
        '@type': 'PostalAddress',
        streetAddress: '8 rue Georges Claude',
        addressLocality: 'Meaux',
        postalCode: '77100',
        addressCountry: 'FR',
      },
    },
    areaServed: {
      '@type': 'City',
      name: loc.name,
      addressRegion: 'Île-de-France',
      addressCountry: 'FR',
    },
    description: seo.description,
  };

  return (
    <>
      <SEO {...seo} path={`/expertise-locale/${ville}`} jsonLd={jsonLd} />
      <main data-testid={`expertise-locale-page-${ville}`}>
        {/* Hero */}
        <section className="pt-32 pb-12">
          <div className="max-w-7xl mx-auto px-6 md:px-12">
            <motion.div {...fadeUp}>
              <p className="text-sm font-medium tracking-[0.2em] uppercase text-[#0099D6] mb-4">
                MÉTÉRIS — {loc.name} ({loc.postalCode})
              </p>
              <h1 className="text-4xl md:text-6xl font-bold tracking-tight text-white mb-6">
                Services informatiques à {loc.name}
              </h1>
              <p className="text-xl text-slate-300 max-w-3xl mb-6">{loc.intro}</p>
              <div className="flex flex-wrap items-center gap-3 text-sm">
                <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#0099D6]/10 border border-[#0099D6]/20 text-[#0099D6]">
                  <MapPin className="w-3.5 h-3.5" />
                  {loc.distanceKm === 0 ? 'Atelier sur place' : `${loc.distanceKm} km depuis Meaux`}
                </span>
                <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/5 border border-white/10 text-slate-300">
                  <Clock className="w-3.5 h-3.5" />
                  Intervention sous 4 h
                </span>
              </div>
            </motion.div>
          </div>
        </section>

        {/* Services grid */}
        <section className="py-16 md:py-24">
          <div className="max-w-7xl mx-auto px-6 md:px-12">
            <motion.div {...fadeUp} className="mb-10">
              <h2 className="text-3xl md:text-4xl font-bold tracking-tight text-white mb-4">
                Nos expertises pour les entreprises de {loc.name}
              </h2>
              <p className="text-lg text-slate-400 max-w-3xl">
                METERIS accompagne {loc.sectors} de {loc.name} et de Seine-et-Marne avec une équipe locale et un atelier physique à 8 rue Georges Claude, Meaux.
              </p>
            </motion.div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {SERVICES.map((s) => {
                const Icon = s.icon;
                return (
                  <motion.div
                    key={s.slug}
                    {...fadeUp}
                    className="bg-[#15171E]/80 border border-white/5 rounded-2xl p-6 hover:border-[#0099D6]/30 transition-colors"
                  >
                    <div className="w-12 h-12 rounded-xl bg-[#0099D6]/10 flex items-center justify-center mb-4">
                      <Icon className="w-6 h-6 text-[#0099D6]" />
                    </div>
                    <h3 className="text-lg font-semibold text-white mb-2">{s.title}</h3>
                    <p className="text-sm text-slate-400 mb-4">{s.description}</p>
                    <Link
                      to={`/solutions/${s.slug}`}
                      className="inline-flex items-center gap-1 text-sm text-[#0099D6] hover:underline font-medium"
                    >
                      En savoir plus
                      <ArrowRight className="w-3.5 h-3.5" />
                    </Link>
                  </motion.div>
                );
              })}
            </div>
          </div>
        </section>

        {/* Pourquoi METERIS */}
        <section className="py-16 md:py-24 bg-[#15171E]/50">
          <div className="max-w-7xl mx-auto px-6 md:px-12">
            <motion.div {...fadeUp}>
              <h2 className="text-3xl md:text-4xl font-bold tracking-tight text-white mb-6">
                Pourquoi choisir METERIS depuis {loc.name} ?
              </h2>
              <ul className="grid grid-cols-1 md:grid-cols-2 gap-4 max-w-4xl">
                <li className="flex gap-3 text-slate-300 leading-relaxed">
                  <CheckCircle className="w-5 h-5 text-[#0099D6] flex-shrink-0 mt-0.5" />
                  <span>
                    Équipe basée à Meaux (77100) — {loc.distanceKm === 0 ? 'à votre porte' : `${loc.distanceKm} km de ${loc.name}`}, intervention sous 4 h via {loc.nearbyHighway}.
                  </span>
                </li>
                <li className="flex gap-3 text-slate-300 leading-relaxed">
                  <CheckCircle className="w-5 h-5 text-[#0099D6] flex-shrink-0 mt-0.5" />
                  <span>
                    Connaissance du tissu économique de {loc.name} : {loc.sectors}.
                  </span>
                </li>
                <li className="flex gap-3 text-slate-300 leading-relaxed">
                  <CheckCircle className="w-5 h-5 text-[#0099D6] flex-shrink-0 mt-0.5" />
                  <span>Audit informatique gratuit et sans engagement.</span>
                </li>
                <li className="flex gap-3 text-slate-300 leading-relaxed">
                  <CheckCircle className="w-5 h-5 text-[#0099D6] flex-shrink-0 mt-0.5" />
                  <span>
                    Atelier physique à Meaux (8 rue Georges Claude) — accueil sur rendez-vous pour particuliers et professionnels.
                  </span>
                </li>
              </ul>
            </motion.div>
          </div>
        </section>

        {/* CTA */}
        <section className="py-16 md:py-24">
          <div className="max-w-4xl mx-auto px-6 md:px-12 text-center">
            <motion.div {...fadeUp}>
              <h2 className="text-3xl md:text-5xl font-bold tracking-tight text-white mb-6">
                Prêt à faire le point sur votre IT à {loc.name} ?
              </h2>
              <p className="text-lg text-slate-300 mb-8">
                Audit gratuit, sans engagement. Premier échange sous 24 h ouvrées.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link
                  to="/contact"
                  className="inline-flex items-center justify-center gap-2 bg-[#0099D6] hover:bg-[#0284C7] text-white font-semibold py-4 px-8 rounded-lg shadow-[0_0_30px_rgba(0,153,214,0.3)] transition-all hover:scale-105"
                >
                  Demander un audit gratuit
                  <ArrowRight className="w-5 h-5" />
                </Link>
                <a
                  href="tel:0183623327"
                  className="inline-flex items-center justify-center gap-2 bg-white/5 hover:bg-white/10 border border-white/10 text-white font-medium py-4 px-8 rounded-lg backdrop-blur-md transition-all"
                >
                  <Phone className="w-5 h-5" />
                  01 83 62 33 27
                </a>
              </div>
            </motion.div>
          </div>
        </section>
      </main>
    </>
  );
}
